import { $buildTokenStream, type TranscriptToken } from './transcript-pause-staging';
import { findAllInterleavedMatches, type MatchableToken } from './transcript-word-match';

import type { TranscriptWordNode } from 'js/src/data/transcript/nodes/TranscriptWordNode';

/**
 * The first covered and last owned transcript word nodes of a matched word span — the two nodes
 * `$getTimeIndicatedBy` turns into a transcript-time range — plus whether any token follows the
 * last owned one (so a split can reject "after the last word").
 */
export type BoundaryNodeSpan = {
    firstNode: TranscriptWordNode;
    lastNode: TranscriptWordNode;
    hasFollowingToken: boolean;
};

const notFoundError = (requestedWords: string[]): string =>
    `The requested word sequence "${requestedWords.join(' ')}" was not found in the transcript. The words must appear consecutively in the transcript, and any <pause seconds="N"/> that falls between them must be included.`;

/**
 * A pause is a real, targetable token, so it stops boundary extension — an unrequested pause is
 * never swallowed. A punctuation-only token (a lone "." between sentences) carries a tiny time span
 * and would become an untargetable sliver if left between two boundaries, so a boundary extends
 * across trailing punctuation to the next actually-spoken word.
 */
function stopsExtension(token: TranscriptToken): boolean {
    return token.kind === 'pause' || /[\p{L}\p{N}]/u.test(token.text);
}

function tokenStartNode(token: TranscriptToken): TranscriptWordNode {
    return token.kind === 'word' ? token.node : token.nodes[0];
}

function tokenEndNode(token: TranscriptToken): TranscriptWordNode {
    return token.kind === 'word' ? token.node : token.nodes[token.nodes.length - 1];
}

/**
 * Build the editor's interleaved (word + pause) token stream, match `requestedWords` against it
 * with the shared tolerant matcher, and resolve the first covered and last owned transcript word
 * nodes of EVERY matched span, in transcript order — the inputs `$getTimeIndicatedBy` needs to
 * compute a cut-/scene-aware transcript-time range. Reusing `$buildTokenStream` (the same stream
 * edit_ai_voiceover reads) means the agent matches exactly the tokens it was shown; running the
 * boundary math through `$getTimeIndicatedBy` (in the caller) means an agent-placed boundary lands
 * byte-for-byte where a human/UI-placed one would.
 *
 * Callers scoped to part of the transcript (a single scene) pick among the spans; everyone else
 * uses {@link $matchBoundaryNodes}. Must run inside an editor read.
 */
export function $matchAllBoundaryNodes(
    requestedWords: string[],
    cutRegions: Array<[number, number]> = [],
): BoundaryNodeSpan[] | { error: string } {
    const stream = $buildTokenStream(cutRegions);
    const matchable: MatchableToken[] = stream.map((token) =>
        token.kind === 'word' ? { word: token.text } : { word: '', isPause: true },
    );

    const matches = findAllInterleavedMatches(matchable, requestedWords);
    if (matches.length === 0) {
        return { error: notFoundError(requestedWords) };
    }

    return matches.map((match) => {
        // Extend the owned end across any trailing punctuation-only tokens to the next spoken
        // word / pause, so the boundary tiles cleanly rather than leaving a punctuation sliver.
        let lastIndex = match.endIndex;
        while (lastIndex + 1 < stream.length && !stopsExtension(stream[lastIndex + 1])) {
            lastIndex++;
        }
        return {
            firstNode: tokenStartNode(stream[match.startIndex]),
            lastNode: tokenEndNode(stream[lastIndex]),
            hasFollowingToken: lastIndex + 1 < stream.length,
        };
    });
}

/** The first transcript-wide match of {@link $matchAllBoundaryNodes}. Must run inside an editor read. */
export function $matchBoundaryNodes(
    requestedWords: string[],
    cutRegions: Array<[number, number]> = [],
): BoundaryNodeSpan | { error: string } {
    const spans = $matchAllBoundaryNodes(requestedWords, cutRegions);
    return 'error' in spans ? spans : spans[0];
}
